/**
 * As regras da Comunicação que não dependem de tela: os recortes da caixa, o
 * filtro inteligente e o preenchimento da mensagem rápida.
 *
 * Sem React, para que "o que casa com o filtro" e "que campo ficou sem valor"
 * se testem sem montar a caixa inteira.
 */

import type { Conversa } from './tipos';

/**
 * As três abas (17/09/2026, decisão do usuário): Abertos, Pendentes e
 * Resolvidos. A situação é calculada, não gravada (0160):
 *
 * - **resolvido**: a conversa foi resolvida;
 * - **aberto**: alguém da equipe aceitou;
 * - **pendente**: ninguém aceitou ainda — a assistente pode estar respondendo,
 *   ou a pessoa pediu alguém da equipe.
 */
export type SituacaoDaConversa = 'aberto' | 'pendente' | 'resolvido';
export type AbaDaComunicacao = 'abertos' | 'pendentes' | 'resolvidos';

export const SITUACAO_DA_ABA: Record<AbaDaComunicacao, SituacaoDaConversa> = {
    abertos: 'aberto', pendentes: 'pendente', resolvidos: 'resolvido',
};

export function situacaoDaConversa(c: Pick<Conversa, 'estado' | 'atendente' | 'atendenteId'>): SituacaoDaConversa {
    if (c.estado === 'resolvida') return 'resolvido';
    return c.atendenteId || c.atendente ? 'aberto' : 'pendente';
}

/**
 * Quem olha a caixa: o nome, para reconhecer a reserva, e se é da gestão —
 * quem administra vê as reservas de todo mundo para poder acompanhar.
 */
export interface QuemVeACaixa {
    nome?: string;
    gestao: boolean;
}

/**
 * Pendente reservada pela distribuição da fila (0161) só aparece para a
 * pessoa escolhida e para a gestão. É recorte de tela: o banco não esconde,
 * e quem abre pelo link da conversa continua vendo.
 */
export function reservadaParaOutro(c: Pick<Conversa, 'reservadaPara' | 'reservadaParaNome'>, quem?: QuemVeACaixa): boolean {
    if (!c.reservadaPara || !quem || quem.gestao) return false;
    return c.reservadaParaNome !== quem.nome;
}

/**
 * A aba, na ordem em que se trabalha: em Pendentes, quem pediu alguém da
 * equipe vem antes; nas outras, a mensagem mais recente primeiro.
 */
export function conversasDaAba(conversas: Conversa[], aba: AbaDaComunicacao, quem?: QuemVeACaixa): Conversa[] {
    const situacao = SITUACAO_DA_ABA[aba];
    const recente = (a: Conversa, b: Conversa) => (b.ultimaMensagemEm ?? '').localeCompare(a.ultimaMensagemEm ?? '');
    return conversas
        .filter((c) => situacaoDaConversa(c) === situacao)
        .filter((c) => situacao !== 'pendente' || !reservadaParaOutro(c, quem))
        .sort((a, b) => (aba === 'pendentes' ? Number(b.conduz === 'humano') - Number(a.conduz === 'humano') : 0) || recente(a, b));
}

/** Motivos oferecidos ao resolver. Texto livre também vale. */
export const MOTIVOS_DE_RESOLUCAO = [
    'Dúvida respondida',
    'Consulta agendada',
    'Orçamento enviado',
    'Paciente não respondeu',
    'Não era da clínica',
];

/** O que a caixa sabe de cada conversa além dela mesma — o que o filtro inteligente lê. */
export interface SinaisDaConversa {
    conversaId: string;
    filaId?: string;
    /** As etiquetas aplicadas à conversa, à mão ou por gatilho. */
    etiquetas: { id: string; nome: string; cor: string; origem: 'manual' | 'gatilho' }[];
    /** Procedimentos na agenda desta pessoa, marcados ou feitos. */
    procedimentos: string[];
    /** O próximo agendamento: é ele que dá {data}, {hora} e {profissional}. */
    proximoAgendamento?: { inicioEm: string; procedimento: string; profissional: string };
    /** O atendimento marcado para HOJE, que o editor abre. */
    atendimentoDeHojeId?: string;
    /** Lead desta pessoa numa etapa em aberto. */
    temOportunidade: boolean;
    /** Orçamento enviado e ainda sem resposta. */
    temProposta: boolean;
}

export type GrupoDoFiltro =
    | 'leitura' | 'conduz'
    | 'canal' | 'atendente' | 'departamento' | 'fila' | 'procedimento'
    | 'agendamento' | 'oportunidade' | 'proposta' | 'etiqueta';

/** Dentro do grupo é OU; entre grupos é E. */
export type SelecaoDoFiltro = Partial<Record<GrupoDoFiltro, string[]>>;

/**
 * A conversa passa no filtro?
 *
 * "Canal 01 ou Canal 02" dentro do grupo, "E fila Recepção" entre grupos: é
 * como a pessoa pensa — "o que entrou pelo comercial e tem proposta em aberto".
 * Etiqueta derivada do cadastro (Paciente, Lead) e etiqueta aplicada entram no
 * mesmo grupo, com prefixos para não se confundirem.
 */
export function casaNoFiltro(c: Conversa, s: SinaisDaConversa | undefined, selecao: SelecaoDoFiltro): boolean {
    return (Object.keys(selecao) as GrupoDoFiltro[]).every((grupo) => {
        const sel = selecao[grupo];
        if (!sel || sel.length === 0) return true;
        switch (grupo) {
            /* As abas antigas viraram filtro: "Não lidas", e quem conduz (Assistente, Humano). */
            case 'leitura': return c.naoLidas > 0;
            case 'conduz': return sel.includes(c.conduz);
            case 'canal': return c.canalId !== null && sel.includes(c.canalId);
            case 'atendente': return c.atendente !== undefined && sel.includes(c.atendente);
            case 'departamento': return c.fila !== undefined && sel.includes(c.fila);
            case 'fila': return s?.filaId !== undefined && sel.includes(s.filaId);
            case 'procedimento': return (s?.procedimentos ?? []).some((p) => sel.includes(p));
            case 'agendamento': return sel.includes(s?.proximoAgendamento ? 'com' : 'sem');
            case 'oportunidade': return Boolean(s?.temOportunidade);
            case 'proposta': return Boolean(s?.temProposta);
            case 'etiqueta':
                return sel.some((v) => (v.startsWith('cadastro:')
                    ? c.vinculos.includes(v.slice('cadastro:'.length))
                    : (s?.etiquetas ?? []).some((e) => `etiqueta:${e.id}` === v)));
            default: return true;
        }
    });
}

export function totalDeFiltros(selecao: SelecaoDoFiltro): number {
    return Object.values(selecao).reduce((soma, v) => soma + (v?.length ?? 0), 0);
}

/**
 * Troca os campos da mensagem rápida pelo que se sabe da conversa.
 *
 * O campo sem valor NÃO some: fica escrito, e volta em `faltando`. Mandar
 * "{data}" ao paciente seria erro visível; apagar em silêncio seria pior —
 * "confirmando seu atendimento de às" chegaria sem ninguém perceber.
 */
export function preencherMensagemRapida(
    texto: string,
    valores: { paciente?: string; data?: string; hora?: string; profissional?: string },
): { texto: string; faltando: string[] } {
    const faltando = new Set<string>();
    const preenchido = texto.replace(/\{(paciente|data|hora|profissional)\}/g, (inteiro, campo: keyof typeof valores) => {
        const valor = valores[campo];
        if (valor) return valor;
        faltando.add(inteiro);
        return inteiro;
    });
    return { texto: preenchido, faltando: [...faltando] };
}

/** Campos entre chaves que ainda estão no texto — o que impede enviar. */
export function camposPorPreencher(texto: string): string[] {
    return [...new Set(texto.match(/\{(paciente|data|hora|profissional)\}/g) ?? [])];
}
